import { AbstractDataProvider } from "./AbstractDataProvider";
import {
  ASFCourseSession,
  AutovioCalendarEvent,
  CourseLesson,
  isASFCourseSession,
  isCourseLesson,
  isTheoryLesson,
  TheoryLesson,
} from "../model/autovioCalendarEvents";
import {
  CreateParams,
  CreateResult,
  DeleteParams,
  DeleteResult,
  GetListParams,
  GetListResult,
  GetManyReferenceParams,
  GetManyReferenceResult,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
} from "react-admin";
import { z } from "zod";
import { DateTime } from "luxon";
import { collection, getDocs, query, where } from "firebase/firestore";
import { firestore } from "../firebase";
import { QueryFieldFilterConstraint } from "@firebase/firestore";
import { autovioCalendarEventConverter } from "./converter/autovioCalendarEventConverter";
import {
  applyFilter,
  applyPagination,
  applySort,
  assertNever,
  normalizeStreet,
  splitCompositeId,
} from "../backoffice.utils";
import { DateTimeFromStringSchema, getAuthenticatedServerClient, runSafelyWithServerClient } from "../api/server.api";
import { isPostalAddress, PostalAddress } from "../model/PostalAddress";
import { OnlineMeeting } from "../model/OnlineMeeting";
import { drivingSchoolsProvider } from "./drivingSchoolsProvider";
import { schemas } from "../generated/serverClient";
import { DateWithoutTime } from "../model/DateWithoutTime";
import { BufferTimesSchema } from "../model/BufferTimes";
import { v4 as randomUuid } from "uuid";

export type CourseEvent = TheoryLesson | ASFCourseSession | CourseLesson;

export function isCourseEvent(event: AutovioCalendarEvent): event is CourseEvent {
  return isTheoryLesson(event) || isASFCourseSession(event) || isCourseLesson(event);
}

export interface CourseAttendee {
  numBookedAppointments: number;
  hasBookedUpcomingAppointment?: true;
}

export interface TheoryOrASFCourse {
  id: string;
  drivingSchoolId: string;
  branchId: string;
  name: "Theoriekurs" | "Aufbauseminar (ASF)";
  startDate: DateWithoutTime;
  endDate: DateWithoutTime;
  start: DateTime;
  end: DateTime;
  location: PostalAddress | OnlineMeeting;
  numEvents: number;
  events: Array<TheoryLesson | ASFCourseSession>;
  numAttendees: number;
  maxAttendees?: number;
  attendees: { [userUid: string]: CourseAttendee };
  isTheoryCourse: boolean;
  isBKFCourse: false;
  timetableId?: never;
}

export type TheoryCourse = Omit<TheoryOrASFCourse, "name" | "isTheoryCourse"> & {
  name: "Theoriekurs";
  isTheoryCourse: true;
};

type CourseDto = z.infer<typeof schemas.CourseDto>;

// I don't know why BKFCourse.location does not have the correct type. :(
// To fix compile errors I added `& { location: PostalAddress }` here ...
export type BKFCourse = z.output<typeof _BKFCourseSchema> & { location: PostalAddress };

export type Course = TheoryOrASFCourse | BKFCourse;

class CoursesProvider extends AbstractDataProvider<Course> {
  async getOne(_resource: string, { id, meta }: GetOneParams<Course>): Promise<GetOneResult<Course>> {
    const { drivingSchoolId, isBKFCourse } = meta ?? {};
    if (isBKFCourse) {
      const serverClient = await getAuthenticatedServerClient();
      const dto = await serverClient.getCourse({ params: { id } });
      return { data: _dtoToRecord(dto) };
    } else {
      const promise1 =
        typeof isBKFCourse === "boolean"
          ? Promise.resolve([])
          : (async () => {
              try {
                const serverClient = await getAuthenticatedServerClient();
                const dto = await serverClient.getCourse({ params: { id } });
                const bkfCourse = _dtoToRecord(dto);
                return [bkfCourse];
              } catch (error) {
                if (!((error as any)?.status === 403 || (error as any)?.status === 404)) {
                  throw error;
                }
                return [];
              }
            })();
      const promise2 = drivingSchoolId
        ? this._fetchTheoryAndASFCourses(drivingSchoolId, where("courseUid", "==", id))
        : Promise.resolve([]);
      const a = await Promise.race([promise1, promise2]);
      if (a.length > 0) {
        return { data: a[0] };
      }
      const a1 = await promise1;
      if (a1.length > 0) {
        return { data: a1[0] };
      }
      const a2 = await promise2;
      if (a2.length > 0) {
        return { data: a2[0] };
      }
      if (drivingSchoolId) {
        throw new Error(`Course ${id} not found`);
      } else {
        throw new Error("meta.drivingSchoolId is required");
      }
    }
  }

  async getList(_resource: string, params: GetListParams): Promise<GetListResult<Course>> {
    const { filter, sort, pagination } = params;
    const { drivingSchoolId, ...clientSideFilter } = filter;
    if (!drivingSchoolId) {
      throw new Error(`Unexpected filter: ${JSON.stringify(filter)} -- expected: {"drivingSchoolId": ...}`);
    }
    const { data: drivingSchool } = await drivingSchoolsProvider.getOne("drivingSchools", { id: drivingSchoolId });
    const promise = this._fetchTheoryAndASFCourses(drivingSchoolId, where("courseUid", ">", ""));
    let courses: Array<Course>;
    if (drivingSchool.customizations.includes("bkfCourses")) {
      const promise2 = this._fetchBKFCourses(drivingSchoolId);
      courses = (await promise).concat(await promise2);
    } else {
      courses = await promise;
    }
    return applyPagination(applySort(applyFilter(courses, clientSideFilter), sort), pagination);
  }

  async getManyReference(resource: string, params: GetManyReferenceParams): Promise<GetManyReferenceResult<Course>> {
    const { target, id, pagination, sort, filter, meta } = params;
    if (target !== "drivingSchoolId") {
      throw new Error(`Invalid target: ${JSON.stringify(target)} -- expected: "drivingSchoolId"`);
    }
    if (typeof id !== "string") {
      throw new Error(`Invalid id: ${JSON.stringify(id)} -- expected: string`);
    }
    if (!id) {
      throw new Error(`Invalid id: ${JSON.stringify(id)} -- must not be the empty string`);
    }
    return this.getList(resource, { pagination, sort, filter: { ...filter, [target]: id }, meta });
  }

  private async _fetchTheoryAndASFCourses(
    drivingSchoolId: string,
    courseUidConstraint: QueryFieldFilterConstraint,
  ): Promise<Array<Course>> {
    const calendarEventsRef = collection(firestore, "calendar_events");
    const docs = (
      await getDocs(
        query(
          calendarEventsRef,
          where("drivingSchoolUid", "==", drivingSchoolId),
          where("deleted", "==", false),
          courseUidConstraint,
        ),
      )
    ).docs;
    const eventsByCourseUid = new Map<string, Array<TheoryLesson | ASFCourseSession>>();
    for (const doc of docs) {
      const event = autovioCalendarEventConverter.fromFirestore(doc);
      if (isTheoryLesson(event) || isASFCourseSession(event)) {
        const array = eventsByCourseUid.get(event.courseUid!);
        if (!array) {
          eventsByCourseUid.set(event.courseUid!, [event]);
        } else {
          // Sort events in array by start (insertion sort) ...
          let i = 0;
          while (i < array.length && array[i].start < event.start) {
            ++i;
          }
          array.splice(i, 0, event as any);
        }
      }
    }
    const courses = Array.from(eventsByCourseUid.entries()).map(([courseUid, events]) => {
      const start = events[0].start;
      const end = events[events.length - 1].end;
      const attendees: { [userUid: string]: CourseAttendee } = {};
      const addAttendee = (userUid: string, forUpcomingEvent: boolean) => {
        let attendee = attendees[userUid];
        if (attendee) {
          attendee.numBookedAppointments += 1;
        } else {
          attendees[userUid] = attendee = { numBookedAppointments: 1 };
        }
        if (forUpcomingEvent) {
          attendee.hasBookedUpcomingAppointment = true;
        }
      };
      let maxAttendees: number | undefined;
      let numAttendees = 0;
      for (const event of events) {
        const isUpcomingEvent = event.start > DateTime.now();
        if (isTheoryLesson(event)) {
          maxAttendees = Math.max(maxAttendees ?? 0, event.maxStudents);
          numAttendees = Math.max(numAttendees, event.numBookedStudents);
          for (const [userUid, student] of Object.entries(event.students)) {
            if (student.rsvp === "accepted") {
              addAttendee(userUid, isUpcomingEvent);
            }
          }
        } else if (isASFCourseSession(event)) {
          numAttendees = Math.max(numAttendees, event.numBookedParticipants);
          for (const userUid of Object.keys(event.participants)) {
            addAttendee(userUid, isUpcomingEvent);
          }
        } else {
          throw new Error(`Unexpected event: ${JSON.stringify(event)}`);
        }
      }
      const course = {
        id: courseUid,
        drivingSchoolId,
        branchId: events[0].branchId,
        location: events[0].location,
        name: _getCourseName(events[0]),
        startDate: DateWithoutTime.fromDateTime(start),
        endDate: DateWithoutTime.fromDateTime(end),
        start,
        end,
        numEvents: events.length,
        events,
        // Don't show more attendees than the maximum, because the important information is the number of free seats ...
        numAttendees: typeof maxAttendees === "number" ? Math.min(numAttendees, maxAttendees) : numAttendees,
        maxAttendees,
        attendees,
        isTheoryCourse: isTheoryLesson(events[0]),
        isBKFCourse: false,
      } satisfies TheoryOrASFCourse;
      if (!course.branchId && isPostalAddress(course.location)) {
        // Fix course.branchId (so that filtering courses by branchId works) ...
        const drivingSchool = drivingSchoolsProvider.getOneFromCache(course.drivingSchoolId);
        if (drivingSchool) {
          const normalizedStreet = normalizeStreet(course.location.street);
          for (const { id: branchId, postalAddress: branchAddress } of drivingSchool.branches) {
            if (
              normalizedStreet === branchAddress.normalizedStreet &&
              course.location.postalCode === branchAddress.postalCode
            ) {
              course.branchId = branchId;
              break;
            }
          }
        }
      }
      return course;
    });
    return courses;
  }

  private async _fetchBKFCourses(drivingSchoolId: string): Promise<Array<Course>> {
    try {
      const serverClient = await getAuthenticatedServerClient();
      const dtos = await serverClient.findCourses({ queries: { drivingSchoolId } });
      const courses = dtos.map(_dtoToRecord);
      return courses;
    } catch (error) {
      if ((error as any)?.status !== 403) {
        throw error;
      }
      return [];
    }
  }

  async create(_: string, params: CreateParams): Promise<CreateResult<Course>> {
    const { data } = params;
    const { drivingSchoolId, theoryLessons } = data;
    if (!drivingSchoolId) {
      throw new Error("drivingSchoolId is required");
    }
    if (theoryLessons) {
      // Create theory course ...
      const serverClient = await getAuthenticatedServerClient();
      const courseId = theoryLessons[0].courseUid;
      await serverClient.createTheoryCourse({ drivingSchoolId, theoryLessons });
      const response = await this.getOne("courses", { id: courseId, meta: { drivingSchoolId } });
      return { data: response.data };
    } else {
      // Create BFK course ...
      const { autoCreateLessons, autoCreateLessonsLecturerId, autoCreateLessonsRoomId, ...createDto } = data as any;
      if (autoCreateLessons) {
        Object.assign(createDto, {
          autoCreateLessons: true,
          autoCreateLessonsLecturerId: autoCreateLessonsLecturerId || null,
          autoCreateLessonsRoomId: autoCreateLessonsRoomId ? splitCompositeId(autoCreateLessonsRoomId)[1] : null,
        });
      }
      const serverClient = await getAuthenticatedServerClient();
      createDto.idempotencyKey = randomUuid();
      const dto = await serverClient.createCourse(createDto);
      return { data: _dtoToRecord(dto) };
    }
  }

  async delete(_: string, params: DeleteParams): Promise<DeleteResult> {
    const { id } = params;
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.deleteCourse(undefined, { params: { id } });
    return { data: {} };
  }

  async update(
    _: string,
    params: UpdateParams<Omit<BKFCourse, "start" | "end"> & { start: DateTime | string; end: DateTime | string }>,
  ): Promise<UpdateResult<Course>> {
    const { id, data } = params;
    let { start, end } = data;
    if (typeof start === "string") {
      start = DateTime.fromISO(start, { zone: "Europe/Berlin" });
    }
    if (typeof end === "string") {
      end = DateTime.fromISO(end, { zone: "Europe/Berlin" });
    }
    const serverClient = await getAuthenticatedServerClient();
    const dto = await serverClient.updateCourse(
      {
        ...(start ? { startDate: start.toISODate() } : {}),
        ...(end ? { endDate: end.toISODate() } : {}),
      },
      { params: { id } },
    );
    return { data: _dtoToRecord(dto) };
  }

  async cloneSafely(
    data: { source: Course; cloneStartDate: DateWithoutTime },
    options: RunSafelyOptions<Course>,
  ): Promise<void> {
    await runSafelyWithServerClient<Course>(async (serverClient) => {
      const dto = await serverClient.cloneCourse(
        { idempotencyKey: randomUuid(), startDate: data.cloneStartDate.toString() },
        { params: { id: data.source.id } },
      );
      return _dtoToRecord(dto);
    }, options);
  }
}

function _getCourseName(lesson: TheoryLesson | ASFCourseSession): "Theoriekurs" | "Aufbauseminar (ASF)" {
  if (isTheoryLesson(lesson)) {
    return "Theoriekurs";
  } else if (isASFCourseSession(lesson)) {
    return "Aufbauseminar (ASF)";
  } else {
    assertNever(lesson);
  }
}

const _BKFCourseSchema = schemas.CourseDto.transform((val) => {
  const startDate = DateWithoutTime.fromString(val.startDate);
  const endDate = DateWithoutTime.fromString(val.endDate);
  const events = _courseDtoEventsToCourseLessons(val);
  return {
    ...val,
    startDate,
    endDate,
    start: startDate.toDateTime(),
    end: endDate.toDateTime().endOf("day"),
    isTheoryCourse: false,
    isBKFCourse: true,
    events,
    ...({
      numEvents: events.length,
      numAttendees: 0,
      attendees: {},
    } as Pick<TheoryOrASFCourse, "numEvents" | "numAttendees" | "maxAttendees" | "attendees">),
  };
});

function _courseDtoEventsToCourseLessons(courseDto: z.infer<typeof schemas.CourseDto>): Array<CourseLesson> {
  return courseDto.events.map((eventDto) => {
    if (eventDto.type !== "CourseLesson" || eventDto.courseId !== courseDto.id || eventDto.deleted) {
      // Should never happen
      throw new Error(`CourseDto for course ${courseDto.id} contained unexpected event: ${JSON.stringify(eventDto)}`);
    }
    const courseLesson = {
      type: "CourseLesson",
      id: eventDto.id,
      drivingSchoolId: courseDto.drivingSchoolId,
      branchId: courseDto.branchId,
      courseId: courseDto.id,
      courseModuleName: eventDto.courseModuleName,
      courseModuleColor: eventDto.courseModuleColor,
      location: courseDto.location,
      roomId: eventDto.roomId ? `${eventDto.drivingSchoolId}:${eventDto.roomId}` : null,
      lecturerId: eventDto.lecturerId,
      instructorId: eventDto.instructorId,
      start: DateTime.fromISO(eventDto.start, { zone: "Europe/Berlin" }),
      end: DateTime.fromISO(eventDto.end, { zone: "Europe/Berlin" }),
      noteForInstructor: "",
      noteForParticipants: eventDto.noteForParticipants,
      bufferTimes: BufferTimesSchema.parse(eventDto.bufferTimes),
      createdAt: DateTimeFromStringSchema.parse(eventDto.createdAt),
      createdBy: eventDto.createdById,
      updatedAt: DateTimeFromStringSchema.parse(eventDto.updatedAt),
      updatedBy: eventDto.updatedById,
      editCount: eventDto.createdAt === eventDto.updatedAt ? 0 : 1,
      editWithReasonCount: 0,
      deleted: false,
    } satisfies CourseLesson;
    return courseLesson;
  });
}

export const coursesProvider = new CoursesProvider();

function _dtoToRecord(dto: CourseDto): Course {
  return _BKFCourseSchema.parse(dto);
}

export function isCourse(record: any): record is Course {
  return !!(record?.id && record.drivingSchoolId && typeof record.numEvents === "number");
}

export function isTheoryCourse(record: any): record is TheoryCourse {
  return isCourse(record) && record.isTheoryCourse;
}
